import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Users, Clock, ChevronRight, Send, User, Mail, Phone, Lock,
  CheckCircle, FileText, Sparkles, ShieldCheck, Award, Upload, X as XIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { QualiopiBadge } from '../components/QualiopiBadge';
import {
  STEPS,
  discoveryChannels,
  particularitiesOptions,
  educationLevels,
  professionalSituations,
  targetFormations,
  handednessOptions,
  sewingLevels,
  motivationTagsOptions,
  fundingOptions,
  goals,
} from './application/formConstants';

// Reusable choice card with hover/selected states
const ChoiceCard = ({ option, selected, onClick, testId, compact = false }) => {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group relative text-left ${compact ? 'p-4' : 'p-5'} rounded-2xl border transition-all duration-300 overflow-hidden ${
        selected
          ? 'bg-[#C9B896]/15 border-[#C9B896] shadow-[0_0_0_3px_rgba(201,184,150,0.15)] -translate-y-0.5'
          : 'bg-white border-[#1F2A4A]/10 hover:border-[#C9B896]/50 hover:-translate-y-0.5'
      }`}
    >
      {Icon && (
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-all ${
          selected ? 'bg-[#C9B896] text-[#0F1623]' : 'bg-[#FAF7F0]/60 text-[#C9B896] group-hover:bg-[#C9B896]/15'
        }`}>
          <Icon className="w-4 h-4" />
        </div>
      )}
      <p className="font-semibold text-[#1F2A4A] text-[14px] mb-0.5 leading-tight">{option.label}</p>
      {option.desc && <p className="text-[11px] text-[#1F2A4A]/75 leading-relaxed">{option.desc}</p>}
      {selected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#C9B896] flex items-center justify-center">
          <CheckCircle className="w-3 h-3 text-[#0F1623]" />
        </div>
      )}
    </button>
  );
};

// Reusable multi-select pill (for motivation_tags / particularities / funding_options)
const MultiPill = ({ value, label, selected, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testId}
    className={`text-left px-4 py-3 rounded-xl border transition-all text-sm font-medium ${
      selected
        ? 'bg-[#C9B896]/15 border-[#C9B896] text-[#1F2A4A]'
        : 'bg-white border-[#1F2A4A]/10 hover:border-[#C9B896]/50 text-[#1F2A4A]'
    }`}
  >
    {selected && <CheckCircle className="inline w-4 h-4 mr-2 text-[#C9B896]" />}
    {label}
  </button>
);

// Label with a "Pré-rempli" badge for fields restored from the user's last application.
// The badge is purely informative — the field stays editable.
const PrefillBadge = ({ show }) => show ? (
  <span
    className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-[#C9B896]/20 text-[#1F2A4A] text-[9px] uppercase tracking-wider font-medium"
    data-testid="prefilled-badge"
    title="Champ pré-rempli depuis ton dernier dossier — modifiable"
  >
    <Sparkles className="w-2.5 h-2.5 text-[#C9B896]" />
    Pré-rempli
  </span>
) : null;

// Compact dropzone used for the mandatory administrative documents
// (CNI + justificatif de domicile) requested on the first application.
const ACCEPTED_DOC_TYPES = '.pdf,.jpg,.jpeg,.png,.webp';
const MAX_DOC_SIZE = 12 * 1024 * 1024; // 12 Mo

const FileDropzone = ({ label, hint, file, onChange, testId }) => {
  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_DOC_SIZE) {
      toast.error(`${f.name} : fichier trop volumineux (max 12 Mo)`);
      e.target.value = '';
      return;
    }
    onChange(f);
  };
  return (
    <div className={`relative rounded-xl border-2 border-dashed transition-colors p-4 ${
      file ? 'border-[#C9B896] bg-[#C9B896]/10' : 'border-[#1F2A4A]/15 bg-white hover:border-[#C9B896]/50'
    }`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${file ? 'bg-[#C9B896] text-[#0F1623]' : 'bg-[#FAF7F0] text-[#C9B896]'}`}>
          {file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1F2A4A] leading-tight">{label} <span className="text-red-500">*</span></p>
          <p className="text-[10px] text-[#1F2A4A]/65 mt-0.5">{hint}</p>
          {file ? (
            <div className="mt-2 flex items-center gap-2">
              <p className="text-[11px] font-mono text-[#1F2A4A] truncate flex-1" title={file.name}>{file.name}</p>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-[#1F2A4A]/60 hover:text-red-600 transition-colors"
                data-testid={`${testId}-remove`}
                aria-label="Retirer le fichier"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-medium text-[#1F2A4A] cursor-pointer hover:text-[#C9B896] transition-colors">
              <input
                type="file"
                accept={ACCEPTED_DOC_TYPES}
                className="hidden"
                onChange={handlePick}
                data-testid={`${testId}-input`}
              />
              Choisir un fichier
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export const ApplicationPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, register, login } = useAuth();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  const [session, setSession] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showLogin, setShowLogin] = useState(false);

  const [formData, setFormData] = useState({
    // User credentials
    email: '', password: '', first_name: '', last_name: '', phone: '',
    // Section 1 — Découverte
    discovery_channels: [],
    referrer_name: '',
    discovery_detail: '',                  // free-text for "Autre"
    // Section 2 — Coordonnées
    nickname: '',
    age: '',
    address_street: '',
    address_complement: '',
    address_city: '',
    address_postcode: '',
    particularities: [],
    // Section 3 — Situation pro & scolaire
    current_situation: '',
    education_level: '',
    professional_history: '',
    current_job_description: '',
    france_travail_id: '',
    france_travail_department: '',
    auto_finance_fallback: '',
    // Section 4 — Formation
    target_formation: '',
    preferred_period: '',
    handedness: '',
    sewing_machine_level: '',
    motivation_tags: [],
    expectations: '',
    motivation: '',
    experience: '',
    // Section 5 — Financement & accessibilité
    has_rqth: '',
    needs_adaptations: '',
    adaptations_detail: '',
    funding_options: [],
    funding_source: '',                    // legacy single-pick (mirrors first funding_options)
    // Section 6 — Divers
    additional_message: '',
    professional_goal: '',                  // legacy
    // Legacy discovery_source kept for analytics (= first channel)
    discovery_source: '',
  });

  // Track which fields were pre-filled from the user's previous application,
  // so we can flag them visually ("Pré-rempli, modifiable").
  const [prefilledFields, setPrefilledFields] = useState(new Set());
  const isPrefilled = (k) => prefilledFields.has(k);

  // First-time applicant flag — kept for legacy logging; new flow always
  // relies on user-level documents uploaded from the student dashboard.
  const [isFirstTimeApplicant, setIsFirstTimeApplicant] = useState(true);
  // Snapshot of user-level documents (CNI, justificatif, RIB) — required
  // before any application can be submitted. Loaded on mount.
  const [docsStatus, setDocsStatus] = useState({
    documents: [],
    all_required_uploaded: false,
    missing_required: [],
    loaded: false,
  });

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  useEffect(() => { loadData(); }, [sessionId]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || ''
      }));
      // Identity fields from the account are always known: mark them prefilled.
      setPrefilledFields(prev => {
        const next = new Set(prev);
        ['email', 'first_name', 'last_name'].forEach((k) => next.add(k));
        if (user.phone) next.add('phone');
        return next;
      });
    }
  }, [user]);

  // Pre-fill from the latest submitted application so a returning student
  // never has to re-type personal data. Only keys with a value overwrite the
  // empty defaults; the candidate can still edit anything before submitting.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const apps = await api.getMyApplications();
        if (cancelled) return;
        // Mark first-time status — only require CNI/justificatif on the very
        // first application of the candidate's life.
        setIsFirstTimeApplicant((apps?.length || 0) === 0);
        // Load user-level documents status — gates submission.
        try {
          const status = await api.getMyDocuments();
          if (!cancelled) setDocsStatus({ ...status, loaded: true });
        } catch {
          if (!cancelled) setDocsStatus((s) => ({ ...s, loaded: true }));
        }
        if (!apps?.length) return;
        // Most recent first (backend already sorts desc, but be defensive).
        const latest = [...apps].sort((a, b) =>
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
        )[0];
        const PREFILL_KEYS = [
          'discovery_channels', 'referrer_name', 'discovery_detail',
          'nickname', 'age', 'address_street', 'address_complement',
          'address_city', 'address_postcode', 'particularities',
          'current_situation', 'education_level', 'professional_history',
          'current_job_description', 'france_travail_id', 'france_travail_department',
          'auto_finance_fallback',
          // target_formation intentionally NOT prefilled — driven by the course
          // the candidate just clicked.
          'preferred_period', 'handedness', 'sewing_machine_level',
          'motivation_tags', 'expectations', 'motivation', 'experience',
          'has_rqth', 'needs_adaptations', 'adaptations_detail',
          'funding_options', 'funding_source', 'additional_message',
          'professional_goal',
        ];
        const prefill = {};
        PREFILL_KEYS.forEach((k) => {
          const v = latest[k];
          if (v !== undefined && v !== null && v !== '' &&
              !(Array.isArray(v) && v.length === 0)) {
            prefill[k] = v;
          }
        });
        if (Object.keys(prefill).length === 0) return;
        // Only overwrite fields that are still at their default empty value,
        // so user choices already made in this session win.
        const justPrefilled = [];
        setFormData(prev => {
          const next = { ...prev };
          for (const [k, val] of Object.entries(prefill)) {
            const cur = prev[k];
            const isEmpty = cur === '' || cur === null || cur === undefined ||
                            (Array.isArray(cur) && cur.length === 0);
            if (isEmpty) {
              next[k] = val;
              justPrefilled.push(k);
            }
          }
          return next;
        });
        if (justPrefilled.length > 0) {
          setPrefilledFields(prev => {
            const nx = new Set(prev);
            justPrefilled.forEach((k) => nx.add(k));
            return nx;
          });
          toast.success(`${justPrefilled.length} champs pré-remplis depuis ton dernier dossier ✓`, { id: 'prefill', duration: 4000 });
        }
      } catch (e) {
        // Silent — prefill is a bonus, not critical.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Pre-fill the target_formation based on the course the candidate clicked
  useEffect(() => {
    if (course?.category && !formData.target_formation) {
      const map = {
        decouverte: 'decouverte',
        complete: 'generale',
        auto: 'auto',
        moto: 'moto',
        nautique: 'nautique',
        habitat: 'habitat',
        dessin: 'dessin',
      };
      const tf = map[course.category];
      if (tf) setFormData(prev => ({ ...prev, target_formation: tf }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  const loadData = async () => {
    try {
      setLoading(true);
      const sessionData = await api.getSession(sessionId);
      setSession(sessionData);
      const courseData = await api.getCourse(sessionData.course_id);
      setCourse(courseData);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Session non trouvée');
      navigate('/formations-sur-place');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(loginData.email, loginData.password);
      toast.success('Connexion réussie !');
      setShowLogin(false);
    } catch (error) {
      toast.error('Email ou mot de passe incorrect');
    }
  };

  // Helpers for multi-selects
  const toggleArr = (field, val) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      const next = arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
      return { ...prev, [field]: next };
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return true; // Intro
      case 2: // Découverte
        if (!formData.discovery_channels.length) {
          toast.error('Sélectionne au moins un canal de découverte');
          return false;
        }
        if (formData.discovery_channels.includes('recommandation') && !formData.referrer_name.trim()) {
          toast.error("Précise le prénom/nom de l'ancien·ne élève qui t'a recommandé·e");
          return false;
        }
        if (formData.discovery_channels.includes('autre') && !formData.discovery_detail.trim()) {
          toast.error('Précise comment tu nous as découverts');
          return false;
        }
        return true;
      case 3: // Situation
        if (!formData.current_situation) {
          toast.error('Indique ta situation actuelle');
          return false;
        }
        if (!formData.education_level) {
          toast.error('Indique ton niveau scolaire');
          return false;
        }
        if (formData.current_situation === 'demandeur_emploi') {
          if (!/^\d{11}$/.test(formData.france_travail_id.trim())) {
            toast.error('Numéro France Travail invalide (11 chiffres attendus)');
            return false;
          }
          if (!/^\d{2,3}$/.test(formData.france_travail_department.trim())) {
            toast.error("Numéro de département invalide (2 ou 3 chiffres)");
            return false;
          }
        }
        return true;
      case 4: // Coordonnées
        if (!formData.first_name || !formData.last_name || !formData.email) {
          toast.error('Prénom, nom et email sont obligatoires');
          return false;
        }
        if (!formData.age || formData.age < 16 || formData.age > 99) {
          toast.error('Indique ton âge (entre 16 et 99 ans)');
          return false;
        }
        if (!formData.phone || formData.phone.length < 8) {
          toast.error('Indique un numéro de téléphone valide');
          return false;
        }
        if (!user && (!formData.password || formData.password.length < 6)) {
          toast.error('Le mot de passe doit contenir au moins 6 caractères');
          return false;
        }
        return true;
      case 5: // Formation
        if (!formData.target_formation) {
          toast.error('Sélectionne la formation visée');
          return false;
        }
        if (!formData.preferred_period.trim()) {
          toast.error('Indique la période souhaitée');
          return false;
        }
        if (!formData.handedness) {
          toast.error('Indique si tu es droitier·e ou gaucher·e');
          return false;
        }
        if (!formData.sewing_machine_level) {
          toast.error("Indique ton niveau d'utilisation d'une machine à coudre");
          return false;
        }
        if (!formData.motivation_tags.length) {
          toast.error('Sélectionne au moins une motivation');
          return false;
        }
        if (!formData.motivation || formData.motivation.length < 50) {
          toast.error('Détaille ta motivation (min. 50 caractères)');
          return false;
        }
        return true;
      case 6: // Financement & accessibilité
        if (!formData.has_rqth) {
          toast.error('Indique si tu as une RQTH');
          return false;
        }
        if (!formData.needs_adaptations) {
          toast.error("Indique si tu as besoin d'adaptations particulières");
          return false;
        }
        if (formData.needs_adaptations === 'oui' && !formData.adaptations_detail.trim()) {
          toast.error("Précise les adaptations nécessaires");
          return false;
        }
        if (!formData.funding_options.length) {
          toast.error('Sélectionne au moins une option de financement');
          return false;
        }
        return true;
      case 7:
        // Block submission if the user's administrative dossier (CNI,
        // justificatif de domicile, RIB) is incomplete. Documents must be
        // uploaded from the student dashboard "Documents" tab.
        if (docsStatus.loaded && !docsStatus.all_required_uploaded) {
          toast.error("Ton dossier administratif est incomplet. Dépose tes documents (CNI, justificatif, RIB) depuis ton espace élève → onglet « Documents ».");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      if (!user) {
        await register({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
        });
      }
      // Pre-compute legacy single-value mirrors for analytics back-compat
      const discoverySourceMirror = formData.discovery_channels[0] || '';
      const fundingSourceMirror = formData.funding_options[0] || '';

      const created = await api.createApplication({
        session_id: sessionId,
        motivation: formData.motivation,
        experience: formData.experience,
        // Legacy compact fields
        funding_source: fundingSourceMirror,
        discovery_source: discoverySourceMirror,
        discovery_detail: formData.discovery_detail || formData.referrer_name || '',
        current_situation: formData.current_situation,
        professional_goal: formData.professional_goal,
        // Extended fields
        discovery_channels: formData.discovery_channels,
        referrer_name: formData.referrer_name || null,
        nickname: formData.nickname || null,
        age: formData.age ? Number(formData.age) : null,
        address_street: formData.address_street || null,
        address_complement: formData.address_complement || null,
        address_city: formData.address_city || null,
        address_postcode: formData.address_postcode || null,
        particularities: formData.particularities,
        education_level: formData.education_level,
        professional_history: formData.professional_history || null,
        current_job_description: formData.current_job_description || null,
        france_travail_id: formData.france_travail_id || null,
        france_travail_department: formData.france_travail_department || null,
        auto_finance_fallback: formData.auto_finance_fallback || null,
        target_formation: formData.target_formation,
        preferred_period: formData.preferred_period,
        handedness: formData.handedness,
        sewing_machine_level: formData.sewing_machine_level,
        motivation_tags: formData.motivation_tags,
        expectations: formData.expectations || null,
        has_rqth: formData.has_rqth,
        needs_adaptations: formData.needs_adaptations,
        adaptations_detail: formData.adaptations_detail || null,
        funding_options: formData.funding_options,
        additional_message: formData.additional_message || null,
      });

      // The backend automatically snapshots the user's documents (CNI,
      // justificatif, RIB) onto this application so the admin Kanban can
      // validate them — no per-application upload needed here.
      toast.success('🎉 Bravo, ta candidature est envoyée !');
      navigate(L('/mes-formations'), { state: { tab: 'applications' } });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns a structured payload when the user's administrative
      // dossier is incomplete — redirect them to the Documents tab.
      if (detail && typeof detail === 'object' && detail.code === 'DOCUMENTS_MISSING') {
        toast.error(detail.message || "Documents administratifs manquants");
        navigate(L('/mes-formations'), { state: { tab: 'applications' } });
      } else {
        toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C9B896] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!session || !course) return null;

  const spotsLeft = session.available_spots - session.enrolled_count;
  const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen pt-20 pb-10 bg-[#FAF7F0]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-[#1F2A4A]/80 mb-3">
          <Link to={L('/')} className="hover:text-[#C9B896] transition-colors">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={L('/formations-sur-place')} className="hover:text-[#C9B896] transition-colors">Formations</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1F2A4A]">Candidature</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* MAIN */}
          <div className="lg:col-span-2">
            <div className="mb-3">
              <p className="text-[10px] font-semibold text-[#C9B896] uppercase tracking-[0.3em] mb-1">
                Change de métier, deviens sellier !
              </p>
              <h1 className="text-2xl md:text-3xl font-heading text-[#1F2A4A]" data-testid="application-title">
                Ton dossier de candidature
              </h1>
              {prefilledFields.size > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9B896]/15 border border-[#C9B896]/40 text-[11px] text-[#1F2A4A]" data-testid="prefill-banner">
                  <Sparkles className="w-3.5 h-3.5 text-[#C9B896]" />
                  <span><strong>{prefilledFields.size}</strong> champs pré-remplis depuis ton dernier dossier — tu peux les modifier librement.</span>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="mb-5">
              <div className="flex justify-between mb-2 gap-1">
                {STEPS.map((step) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => { if (isCompleted) setCurrentStep(step.id); }}
                      className={`flex flex-col items-center flex-1 min-w-0 ${isCompleted ? 'cursor-pointer' : 'cursor-default'}`}
                      data-testid={`step-jump-${step.id}`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted ? 'bg-[#C9B896] text-[#0F1623]'
                          : isActive ? 'bg-[#C9B896]/20 border-2 border-[#C9B896] text-[#C9B896] ring-4 ring-[#C9B896]/10'
                          : 'bg-white text-[#1F2A4A]/55'
                      }`}>
                        {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                      </div>
                      <span className={`mt-1.5 text-[9px] font-medium uppercase tracking-wider hidden md:block truncate ${
                        isActive || isCompleted ? 'text-[#1F2A4A]' : 'text-[#1F2A4A]/55'
                      }`}>{step.name}</span>
                    </button>
                  );
                })}
              </div>
              <div className="h-1 rounded-full bg-white overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#C9B896] to-[#E0D4BA] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[10px] text-[#1F2A4A]/70 mt-1.5 text-center md:text-right">
                Étape {currentStep} sur {STEPS.length} · {Math.round(progress)}% complété
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 md:p-6">
              {/* ─────────── STEP 1 — Intro ─────────── */}
              {currentStep === 1 && (
                <div className="animate-fade-in text-center py-2">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#C9B896] to-[#A89668] mb-4 shadow-md shadow-[#C9B896]/20">
                    <Sparkles className="w-7 h-7 text-[#0F1623]" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-heading text-[#1F2A4A] mb-2">
                    {user ? <>Bon retour parmi nous, <span className="text-[#C9B896]">{user.first_name}</span>.</> : 'Bienvenue dans ton parcours candidat.'}
                  </h2>
                  <p className="text-[#1F2A4A] max-w-lg mx-auto text-sm leading-relaxed mb-4">
                    {prefilledFields.size > 0
                      ? <>On a déjà rempli <strong className="text-[#C9B896]">{prefilledFields.size} champs</strong> depuis ton dernier dossier. Vérifie, complète, et envoie en quelques clics.</>
                      : <>En <strong className="text-[#C9B896]">7 étapes courtes</strong>, tu vas remplir ton dossier officiel. Les informations préparent ton financement et adaptent la formation à ton profil.</>}
                  </p>
                  <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto mb-5">
                    {[
                      { icon: Clock, label: '~ 5 min' },
                      { icon: ShieldCheck, label: 'RGPD' },
                      { icon: Award, label: 'Qualiopi' },
                    ].map((b, i) => {
                      const I = b.icon;
                      return (
                        <div key={i} className="p-2 rounded-lg bg-white border border-[#1F2A4A]/10">
                          <I className="w-3.5 h-3.5 text-[#C9B896] mx-auto mb-1" />
                          <p className="text-[10px] font-medium text-[#1F2A4A]">{b.label}</p>
                        </div>
                      );
                    })}
                  </div>
                  {!user && (
                    <p className="text-xs text-[#1F2A4A]/85">
                      Déjà inscrit·e&nbsp;?{' '}
                      <button onClick={() => setShowLogin(true)} className="text-[#C9B896] underline underline-offset-4 hover:text-[#1F2A4A]" data-testid="login-link">
                        Connecte-toi
                      </button>
                    </p>
                  )}
                  {showLogin && !user && (
                    <form onSubmit={handleLogin} className="mt-4 max-w-sm mx-auto text-left space-y-2 p-4 bg-white border border-[#1F2A4A]/10 rounded-xl">
                      <div className="space-y-1">
                        <Label className="text-[#1F2A4A] text-xs">Email</Label>
                        <Input type="email" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} className="bg-[#FAF7F0] border-[#1F2A4A]/10 h-10 rounded-lg text-[#1F2A4A]" data-testid="quick-login-email" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[#1F2A4A] text-xs">Mot de passe</Label>
                        <Input type="password" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} className="bg-[#FAF7F0] border-[#1F2A4A]/10 h-10 rounded-lg text-[#1F2A4A]" data-testid="quick-login-password" />
                      </div>
                      <Button type="submit" className="w-full bg-[#C9B896] text-[#0F1623] hover:bg-[#E0D4BA] h-10 rounded-lg" data-testid="quick-login-submit">Se connecter</Button>
                    </form>
                  )}
                </div>
              )}

              {/* ─────────── STEP 2 — Découverte ─────────── */}
              {currentStep === 2 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Section 1 / 6</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">Comment nous as-tu connus&nbsp;?</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">Plusieurs réponses possibles. Cela nous aide à mieux servir nos futurs élèves.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {discoveryChannels.map((opt) => (
                      <ChoiceCard
                        key={opt.value}
                        option={opt}
                        selected={formData.discovery_channels.includes(opt.value)}
                        onClick={() => toggleArr('discovery_channels', opt.value)}
                        testId={`discovery-${opt.value}`}
                        compact
                      />
                    ))}
                  </div>
                  {formData.discovery_channels.includes('recommandation') && (
                    <div className="pt-2 animate-fade-in">
                      <Label className="text-[#1F2A4A] text-sm">Prénom / nom de l'ancien·ne élève *</Label>
                      <Input
                        value={formData.referrer_name}
                        onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })}
                        className="mt-2 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]"
                        placeholder="Ex : Marie Dupont — promo 2024"
                        data-testid="referrer-name"
                      />
                    </div>
                  )}
                  {formData.discovery_channels.includes('autre') && (
                    <div className="pt-2 animate-fade-in">
                      <Label className="text-[#1F2A4A] text-sm">Précise comment *</Label>
                      <Input
                        value={formData.discovery_detail}
                        onChange={(e) => setFormData({ ...formData, discovery_detail: e.target.value })}
                        className="mt-2 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]"
                        placeholder="Salon, podcast, magazine, etc."
                        data-testid="discovery-other-detail"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ─────────── STEP 3 — Situation pro & scolaire ─────────── */}
              {currentStep === 3 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Section 3 / 6</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">Ta situation professionnelle</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">Indique ton statut actuel et ton parcours pour préparer ton dossier de financement.</p>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block">Situation actuelle *</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {professionalSituations.map((opt) => (
                        <ChoiceCard
                          key={opt.value}
                          option={opt}
                          selected={formData.current_situation === opt.value}
                          onClick={() => setFormData({ ...formData, current_situation: opt.value })}
                          testId={`situation-${opt.value}`}
                          compact
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block">Niveau scolaire — Diplôme le plus élevé *</Label>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {educationLevels.map((opt) => (
                        <MultiPill
                          key={opt.value}
                          value={opt.value}
                          label={opt.label}
                          selected={formData.education_level === opt.value}
                          onToggle={() => setFormData({ ...formData, education_level: opt.value })}
                          testId={`education-${opt.value}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Décris brièvement ton parcours professionnel</Label>
                    <Textarea
                      value={formData.professional_history}
                      onChange={(e) => setFormData({ ...formData, professional_history: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[110px]"
                      placeholder="Métiers exercés, durée, secteurs…"
                      data-testid="prof-history"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Missions dans ton poste actuel</Label>
                    <Textarea
                      value={formData.current_job_description}
                      onChange={(e) => setFormData({ ...formData, current_job_description: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[90px]"
                      placeholder="Si tu travailles actuellement, décris en quelques lignes…"
                      data-testid="current-job-desc"
                    />
                  </div>

                  {formData.current_situation === 'demandeur_emploi' && (
                    <div className="p-5 rounded-xl bg-[#C9B896]/10 border border-[#C9B896]/40 space-y-4 animate-fade-in">
                      <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-wider">France Travail — informations obligatoires</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#1F2A4A] text-sm">Numéro France Travail (11 chiffres) *</Label>
                          <Input
                            value={formData.france_travail_id}
                            onChange={(e) => setFormData({ ...formData, france_travail_id: e.target.value.replace(/\D/g, '') })}
                            className="bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]"
                            placeholder="12345678901"
                            maxLength={11}
                            data-testid="ft-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#1F2A4A] text-sm">N° de département de ton agence *</Label>
                          <Input
                            value={formData.france_travail_department}
                            onChange={(e) => setFormData({ ...formData, france_travail_department: e.target.value.replace(/\D/g, '') })}
                            className="bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]"
                            placeholder="13"
                            maxLength={3}
                            data-testid="ft-department"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[#1F2A4A] text-sm mb-2 block">En cas de refus de prise en charge, peux-tu autofinancer ?</Label>
                        <div className="grid sm:grid-cols-2 gap-2.5">
                          <MultiPill
                            value="oui"
                            label="OUI, je pourrai autofinancer"
                            selected={formData.auto_finance_fallback === 'oui'}
                            onToggle={() => setFormData({ ...formData, auto_finance_fallback: 'oui' })}
                            testId="auto-finance-oui"
                          />
                          <MultiPill
                            value="non"
                            label="NON, j'abandonnerai le projet"
                            selected={formData.auto_finance_fallback === 'non'}
                            onToggle={() => setFormData({ ...formData, auto_finance_fallback: 'non' })}
                            testId="auto-finance-non"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ─────────── STEP 4 — Coordonnées ─────────── */}
              {currentStep === 4 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Section 2 / 6</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">Tes coordonnées</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">
                      {user ? `Bonjour ${user.first_name} ! Complète tes coordonnées.` : "Nous créons ton compte en même temps que ton dossier."}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">Prénom * <PrefillBadge show={isPrefilled('first_name')} /></Label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9B896]/80" />
                        <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className={`pl-10 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('first_name') ? 'border-[#C9B896]/50' : ''}`} placeholder="Ton prénom" data-testid="application-firstname" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">NOM * <PrefillBadge show={isPrefilled('last_name')} /></Label>
                      <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('last_name') ? 'border-[#C9B896]/50' : ''}`} placeholder="Ton nom" data-testid="application-lastname" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">Surnom (optionnel) <PrefillBadge show={isPrefilled('nickname')} /></Label>
                      <Input value={formData.nickname} onChange={(e) => setFormData({ ...formData, nickname: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('nickname') ? 'border-[#C9B896]/50' : ''}`} placeholder="Comment tes proches t'appellent" data-testid="application-nickname" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">Âge * <PrefillBadge show={isPrefilled('age')} /></Label>
                      <Input type="number" min="16" max="99" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('age') ? 'border-[#C9B896]/50' : ''}`} placeholder="Ton âge" data-testid="application-age" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">Email * <PrefillBadge show={isPrefilled('email')} /></Label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9B896]/80" />
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} disabled={!!user} className={`pl-10 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('email') ? 'border-[#C9B896]/50' : ''}`} placeholder="toi@email.com" data-testid="application-email" />
                      </div>
                      {!!user && <p className="text-[10px] text-[#1F2A4A]/55">Email du compte — non modifiable ici. <Link to={L('/profil')} className="text-[#C9B896] underline">Modifier le profil</Link></p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A] inline-flex items-center">Téléphone * <PrefillBadge show={isPrefilled('phone')} /></Label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9B896]/80" />
                        <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={`pl-10 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('phone') ? 'border-[#C9B896]/50' : ''}`} placeholder="06 12 34 56 78" data-testid="application-phone" />
                      </div>
                    </div>
                  </div>

                  {!user && (
                    <div className="space-y-2">
                      <Label className="text-[#1F2A4A]">Crée un mot de passe *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9B896]/80" />
                        <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="pl-10 bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]" placeholder="6 caractères minimum" data-testid="application-password" />
                      </div>
                      <p className="text-xs text-[#1F2A4A]/80">Il te servira à suivre ta candidature.</p>
                    </div>
                  )}

                  {/* Adresse */}
                  <div className="pt-4 border-t border-[#1F2A4A]/10">
                    <Label className="text-[#1F2A4A] mb-3 block font-medium inline-flex items-center">
                      Adresse postale
                      <PrefillBadge show={isPrefilled('address_street') || isPrefilled('address_city') || isPrefilled('address_postcode')} />
                    </Label>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-xs text-[#1F2A4A]/75">Numéro et rue</Label>
                        <Input value={formData.address_street} onChange={(e) => setFormData({ ...formData, address_street: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('address_street') ? 'border-[#C9B896]/50' : ''}`} placeholder="12 rue des Selliers" data-testid="address-street" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-xs text-[#1F2A4A]/75">Complément d'adresse</Label>
                        <Input value={formData.address_complement} onChange={(e) => setFormData({ ...formData, address_complement: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('address_complement') ? 'border-[#C9B896]/50' : ''}`} placeholder="Bâtiment, étage…" data-testid="address-complement" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-[#1F2A4A]/75">Code postal</Label>
                        <Input value={formData.address_postcode} onChange={(e) => setFormData({ ...formData, address_postcode: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('address_postcode') ? 'border-[#C9B896]/50' : ''}`} placeholder="13800" data-testid="address-postcode" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-[#1F2A4A]/75">Ville</Label>
                        <Input value={formData.address_city} onChange={(e) => setFormData({ ...formData, address_city: e.target.value })} className={`bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A] ${isPrefilled('address_city') ? 'border-[#C9B896]/50' : ''}`} placeholder="Istres" data-testid="address-city" />
                      </div>
                    </div>
                  </div>

                  {/* Particularités */}
                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Particularités</Label>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {particularitiesOptions.map((opt) => (
                        <MultiPill
                          key={opt.value}
                          value={opt.value}
                          label={opt.label}
                          selected={formData.particularities.includes(opt.value)}
                          onToggle={() => toggleArr('particularities', opt.value)}
                          testId={`particularity-${opt.value}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ─────────── STEP 5 — Formation ─────────── */}
              {currentStep === 5 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Section 4 / 6</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">À propos de la formation</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">Aide-nous à préparer ton équipement et adapter ton parcours.</p>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Formation visée *</Label>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {targetFormations.map((opt) => (
                        <MultiPill
                          key={opt.value}
                          value={opt.value}
                          label={`${opt.label} · ${opt.desc}`}
                          selected={formData.target_formation === opt.value}
                          onToggle={() => setFormData({ ...formData, target_formation: opt.value })}
                          testId={`target-${opt.value}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Période souhaitée *</Label>
                    <Input
                      value={formData.preferred_period}
                      onChange={(e) => setFormData({ ...formData, preferred_period: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 h-11 rounded-xl text-[#1F2A4A]"
                      placeholder="Ex : Septembre 2026 OU dates précises connues"
                      data-testid="preferred-period"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-[#1F2A4A] mb-3 block font-medium">Latéralité *</Label>
                      <div className="grid grid-cols-2 gap-2.5">
                        {handednessOptions.map((opt) => (
                          <MultiPill
                            key={opt.value}
                            value={opt.value}
                            label={opt.label}
                            selected={formData.handedness === opt.value}
                            onToggle={() => setFormData({ ...formData, handedness: opt.value })}
                            testId={`handedness-${opt.value}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#1F2A4A] mb-3 block font-medium">Niveau machine à coudre indus. *</Label>
                      <div className="grid grid-cols-2 gap-2.5">
                        {sewingLevels.map((opt) => (
                          <MultiPill
                            key={opt.value}
                            value={opt.value}
                            label={opt.label}
                            selected={formData.sewing_machine_level === opt.value}
                            onToggle={() => setFormData({ ...formData, sewing_machine_level: opt.value })}
                            testId={`sewing-${opt.value}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Motivations *</Label>
                    <p className="text-xs text-[#1F2A4A]/75 mb-3">Plusieurs choix possibles</p>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {motivationTagsOptions.map((opt) => (
                        <MultiPill
                          key={opt.value}
                          value={opt.value}
                          label={opt.label}
                          selected={formData.motivation_tags.includes(opt.value)}
                          onToggle={() => toggleArr('motivation_tags', opt.value)}
                          testId={`motivation-${opt.value}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Raconte-nous ta motivation détaillée *</Label>
                    <Textarea
                      value={formData.motivation}
                      onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[120px]"
                      placeholder="Ton projet, ce qui t'attire dans la sellerie, où tu te vois dans 2 ans…"
                      data-testid="application-motivation"
                    />
                    <div className="flex items-center justify-between text-xs">
                      <span className={formData.motivation.length >= 50 ? 'text-green-600' : 'text-[#1F2A4A]/70'}>
                        {formData.motivation.length}/50 caractères minimum
                      </span>
                      {formData.motivation.length >= 50 && <span className="text-green-600">✓ Parfait&nbsp;!</span>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Tes attentes personnelles vis-à-vis de cette formation</Label>
                    <Textarea
                      value={formData.expectations}
                      onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[90px]"
                      placeholder="Optionnel — ce que tu attends concrètement à l'issue de la formation."
                      data-testid="expectations"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Expérience en sellerie</Label>
                    <Textarea
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[80px]"
                      placeholder="Optionnel — bricolage, autres formations…"
                      data-testid="application-experience"
                    />
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Ton projet à long terme</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {goals.map((opt) => (
                        <ChoiceCard
                          key={opt.value}
                          option={opt}
                          selected={formData.professional_goal === opt.value}
                          onClick={() => setFormData({ ...formData, professional_goal: opt.value })}
                          testId={`goal-${opt.value}`}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ─────────── STEP 6 — Financement & accessibilité ─────────── */}
              {currentStep === 6 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Section 5 / 6</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">Financement &amp; accessibilité</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">Tu es seul·e légalement habilité·e à réaliser les démarches. Nous te guidons pas à pas.</p>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">As-tu une Reconnaissance Travailleur Handicapé (RQTH) ? *</Label>
                    <div className="grid grid-cols-2 gap-2.5">
                      {[{ v: 'oui', l: 'Oui' }, { v: 'non', l: 'Non' }].map((o) => (
                        <MultiPill
                          key={o.v}
                          value={o.v}
                          label={o.l}
                          selected={formData.has_rqth === o.v}
                          onToggle={() => setFormData({ ...formData, has_rqth: o.v })}
                          testId={`rqth-${o.v}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Besoin d'adaptations particulières ? *</Label>
                    <p className="text-xs text-[#1F2A4A]/75 mb-3">Situation de handicap, spécificités du métier, ou difficulté liée à l'apprentissage.</p>
                    <div className="grid grid-cols-2 gap-2.5">
                      {[{ v: 'oui', l: 'OUI' }, { v: 'non', l: 'NON' }].map((o) => (
                        <MultiPill
                          key={o.v}
                          value={o.v}
                          label={o.l}
                          selected={formData.needs_adaptations === o.v}
                          onToggle={() => setFormData({ ...formData, needs_adaptations: o.v })}
                          testId={`adapt-${o.v}`}
                        />
                      ))}
                    </div>
                    {formData.needs_adaptations === 'oui' && (
                      <div className="mt-4 animate-fade-in">
                        <Label className="text-[#1F2A4A] text-sm">Si OUI, précise lesquelles *</Label>
                        <Textarea
                          value={formData.adaptations_detail}
                          onChange={(e) => setFormData({ ...formData, adaptations_detail: e.target.value })}
                          className="mt-2 bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[80px]"
                          placeholder="Décris précisément les adaptations nécessaires"
                          data-testid="adaptations-detail"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#1F2A4A] mb-3 block font-medium">Financements envisagés *</Label>
                    <p className="text-xs text-[#1F2A4A]/75 mb-3">Plusieurs choix possibles.</p>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {fundingOptions.map((opt) => (
                        <MultiPill
                          key={opt.value}
                          value={opt.value}
                          label={opt.label}
                          selected={formData.funding_options.includes(opt.value)}
                          onToggle={() => toggleArr('funding_options', opt.value)}
                          testId={`funding-${opt.value}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Qualiopi block — hidden at client request (visible on
                      formation pages and footer only). */}
                  {false && (
                  <div className="p-5 bg-[#C9B896]/10 rounded-xl border border-[#C9B896]/60">
                    <div className="flex items-start gap-4">
                      <QualiopiBadge size="lg" className="shrink-0" />
                      <div>
                        <p className="font-medium text-[#1F2A4A] mb-1">Certifié Qualiopi</p>
                        <p className="text-sm text-[#1F2A4A] leading-relaxed">
                          Plusieurs solutions de financement disponibles selon votre situation.
                        </p>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* ─────────── STEP 7 — Validation & message ─────────── */}
              {currentStep === 7 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <p className="text-xs text-[#C9B896] font-semibold uppercase tracking-wider mb-2">Dernière ligne droite 🏁</p>
                    <h2 className="text-2xl font-heading text-[#1F2A4A] mb-2">Récapitulatif &amp; envoi</h2>
                    <p className="text-[#1F2A4A]/85 text-sm">Vérifie tes informations et ajoute un mot personnel si tu le souhaites.</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#1F2A4A]">Message — informations diverses</Label>
                    <Textarea
                      value={formData.additional_message}
                      onChange={(e) => setFormData({ ...formData, additional_message: e.target.value })}
                      className="bg-white border-[#1F2A4A]/10 rounded-xl text-[#1F2A4A] min-h-[110px]"
                      placeholder="Tout ce qu'on devrait savoir et que les autres champs n'ont pas couvert."
                      data-testid="additional-message"
                    />
                  </div>

                  {/* Statut du dossier administratif — gating */}
                  <div className="pt-4 border-t border-[#1F2A4A]/10" data-testid="admin-docs-status-block">
                    <div className="flex items-start gap-3 mb-3">
                      <ShieldCheck className="w-5 h-5 text-[#C9B896] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-wider mb-1">Pièces administratives</p>
                        <h3 className="text-base font-medium text-[#1F2A4A]">Dossier administratif</h3>
                      </div>
                    </div>
                    {!docsStatus.loaded ? (
                      <div className="text-xs text-[#1F2A4A]/60 px-4 py-3 rounded-xl bg-white border border-[#1F2A4A]/10">
                        Vérification de tes documents…
                      </div>
                    ) : docsStatus.all_required_uploaded ? (
                      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-200" data-testid="admin-docs-ok">
                        <CheckCircle className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
                        <div className="text-xs">
                          <p className="font-medium text-green-800">Dossier complet — tu peux soumettre.</p>
                          <p className="text-green-700/85 mt-0.5">
                            Tes documents seront automatiquement attachés à cette candidature.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="admin-docs-missing">
                        <XIcon className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-xs flex-1">
                          <p className="font-medium text-amber-800">Documents manquants — la soumission est bloquée.</p>
                          <p className="text-amber-700/85 mt-0.5">
                            Dépose les pièces obligatoires depuis ton espace élève&nbsp;: <strong>Mes formations → onglet «&nbsp;Documents&nbsp;»</strong>.
                          </p>
                          <Link
                            to={withLangPrefix('/mes-formations', lang)}
                            state={{ tab: 'applications' }}
                            className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#1F2A4A] hover:text-[#C9B896] transition-colors underline"
                            data-testid="goto-docs-tab"
                          >
                            Aller au dépôt de documents →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recap */}
                  <div className="pt-4 border-t border-[#1F2A4A]/10">
                    <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-wider mb-3">Récapitulatif</p>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-white border border-[#1F2A4A]/8 rounded-lg">
                        <p className="text-[#1F2A4A]/80 text-xs mb-1">Candidat</p>
                        <p className="text-[#1F2A4A] font-medium">{formData.first_name} {formData.last_name}{formData.age ? ` · ${formData.age} ans` : ''}</p>
                      </div>
                      <div className="p-3 bg-white border border-[#1F2A4A]/8 rounded-lg">
                        <p className="text-[#1F2A4A]/80 text-xs mb-1">Formation visée</p>
                        <p className="text-[#1F2A4A] font-medium">{targetFormations.find(t => t.value === formData.target_formation)?.label || '—'}</p>
                      </div>
                      <div className="p-3 bg-white border border-[#1F2A4A]/8 rounded-lg">
                        <p className="text-[#1F2A4A]/80 text-xs mb-1">Situation</p>
                        <p className="text-[#1F2A4A] font-medium">{professionalSituations.find(s => s.value === formData.current_situation)?.label || '—'}</p>
                      </div>
                      <div className="p-3 bg-white border border-[#1F2A4A]/8 rounded-lg">
                        <p className="text-[#1F2A4A]/80 text-xs mb-1">Financement</p>
                        <p className="text-[#1F2A4A] font-medium">{formData.funding_options.map(v => fundingOptions.find(f => f.value === v)?.label).filter(Boolean).join(', ') || '—'}</p>
                      </div>
                      <div className="p-3 bg-white border border-[#1F2A4A]/8 rounded-lg">
                        <p className="text-[#1F2A4A]/80 text-xs mb-1">Période souhaitée</p>
                        <p className="text-[#1F2A4A] font-medium">{formData.preferred_period || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1F2A4A]/10">
                {currentStep > 1 ? (
                  <Button variant="outline" onClick={prevStep} className="border-[#C9B896]/50 text-[#1F2A4A] hover:bg-[#C9B896]/20 rounded-xl" data-testid="step-back">
                    Retour
                  </Button>
                ) : (
                  <Link to={L(`/formation/${course.id}`)}>
                    <Button variant="outline" className="border-[#C9B896]/50 text-[#1F2A4A] hover:bg-[#C9B896]/20 rounded-xl">
                      Annuler
                    </Button>
                  </Link>
                )}

                {currentStep < STEPS.length ? (
                  <Button onClick={nextStep} className="bg-[#C9B896] text-[#0F1623] hover:bg-[#E0D4BA] rounded-xl px-8 font-medium btn-premium" data-testid="step-next">
                    {currentStep === 1 ? 'Démarrer le dossier' : 'Continuer'} →
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={submitting} className="bg-[#C9B896] text-[#0F1623] hover:bg-[#E0D4BA] rounded-xl px-8 font-medium btn-premium" data-testid="submit-application">
                    {submitting ? 'Envoi…' : (<><Send className="w-4 h-4 mr-2" /> Valider ma candidature</>)}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div>
            <div className="glass-card rounded-2xl p-5 sticky top-20">
              <span className="text-[10px] font-semibold text-[#C9B896] uppercase tracking-[0.25em]">Formation visée</span>
              <h3 className="font-heading text-lg text-[#1F2A4A] mt-2 mb-4">{course.title}</h3>

              <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-2.5 text-[#1F2A4A]">
                  <Calendar className="w-4 h-4 text-[#C9B896] shrink-0" />
                  <div>
                    <div className="font-medium text-[#1F2A4A] text-xs">{format(parseISO(session.start_date), 'dd MMMM yyyy', { locale: fr })}</div>
                    <div className="text-[#1F2A4A]/85 text-[10px]">au {format(parseISO(session.end_date), 'dd MMMM yyyy', { locale: fr })}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 text-[#1F2A4A] text-xs">
                  <Clock className="w-4 h-4 text-[#C9B896] shrink-0" />
                  <span>{course.duration_days} jours de formation</span>
                </div>
                <div className="flex items-center gap-2.5 text-[#1F2A4A] text-xs">
                  <MapPin className="w-4 h-4 text-[#C9B896] shrink-0" />
                  <span>{session.location}</span>
                </div>
                <div className="flex items-center gap-2.5 text-[#1F2A4A] text-xs">
                  <Users className="w-4 h-4 text-[#C9B896] shrink-0" />
                  <span>{spotsLeft} places disponibles</span>
                </div>

                <div className="pt-3 mt-3 border-t border-[#1F2A4A]/10 flex items-center justify-between">
                  <span className="text-[#1F2A4A]/85 text-xs">Prix</span>
                  <span className="text-xl font-heading text-[#C9B896]">{course.price.toFixed(0)} €</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#1F2A4A]/10">
                <p className="text-[10px] text-[#C9B896] font-semibold uppercase tracking-wider mb-2">RGPD &amp; confidentialité</p>
                <div className="flex items-center gap-2 p-2.5 bg-[#FAF7F0]/70 rounded-xl border border-[#1F2A4A]/10">
                  <ShieldCheck className="w-4 h-4 text-[#C9B896] shrink-0" />
                  <p className="text-[10px] text-[#1F2A4A]/85 leading-relaxed">
                    Tes informations restent strictement utilisées pour ton dossier.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
